import React from 'react';
import { BranchItem } from '../../types';
import { useContent } from '../../context/ContentContext';
import { createWhatsAppLink } from '../../utils/formatters';
import { resolveMediaUrl } from '../../utils/api';
import { LetGoCardsGallery } from './LetGoCardsGallery';
import { motion } from 'motion/react';
import { MapPin, Clock, MessageCircle, ExternalLink, Navigation, Store } from 'lucide-react';

interface ChapterSectionProps {
  branch: BranchItem;
  reversed?: boolean;
}

export const ChapterSection: React.FC<ChapterSectionProps> = ({ branch, reversed = false }) => {
  const { data } = useContent();
  const { contactSettings } = data;

  const branchWALink = createWhatsAppLink(
    contactSettings.whatsapp,
    `Halo LetonKu ${branch.branchName}, saya ingin menanyakan meja dan pemesanan kopi.`
  );

  const resolvedBg = resolveMediaUrl(branch.bgImage);

  return (
    <section
      id={branch.id}
      className="relative w-full overflow-hidden py-8 sm:py-14 px-4 sm:px-6 lg:px-8 bg-[#F8F5EF] font-body"
    >
      <div className="max-w-7xl mx-auto w-full">
        {/* Card Layout */}
        <div className="bg-[#FFFFFF] rounded-3xl border border-[#E8E0D6] shadow-[0_8px_30px_rgba(43,29,23,0.06)] overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
          {/* Image Column (5 cols) */}
          <div
            className={`lg:col-span-5 relative min-h-[260px] sm:min-h-[360px] lg:min-h-full bg-[#F8F5EF] ${
              reversed ? 'order-1 lg:order-2' : 'order-1 lg:order-1'
            }`}
          >
            {resolvedBg ? (
              <img
                src={resolvedBg}
                alt={branch.branchName}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (target.src !== '/assets/leton-coffee-beans.jpg' && !target.src.endsWith('/assets/leton-coffee-beans.jpg')) {
                    target.src = '/assets/leton-coffee-beans.jpg';
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#39271F]">
                <Store className="w-16 h-16 opacity-35" />
              </div>
            )}
            <div className="absolute top-4 left-4">
              <span className="px-3.5 py-1.5 rounded-full bg-[#FFFFFF]/95 backdrop-blur-md text-[#39271F] border border-[#E8E0D6] text-xs font-semibold uppercase tracking-wider shadow-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                <span>Buka Sekarang</span>
              </span>
            </div>
          </div>

          {/* Details Column (7 cols) */}
          <div
            className={`lg:col-span-7 p-6 sm:p-10 lg:p-12 flex flex-col justify-between ${
              reversed ? 'order-2 lg:order-1' : 'order-2 lg:order-2'
            }`}
          >
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#B77945]/12 text-[#B77945] border border-[#B77945]/20 text-xs font-semibold uppercase tracking-wider mb-4">
                <span>{branch.chapterNumber} — {branch.chapterName}</span>
                {branch.badge && <span className="text-[#756B64]">/ {branch.badge}</span>}
              </div>

              {/* Title */}
              <h2 className="font-heading font-bold text-2xl sm:text-4xl text-[#2B1D17] tracking-[-0.02em] leading-tight">
                {branch.branchName}
              </h2>

              {/* Tagline */}
              {branch.tagline && (
                <p className="mt-2 text-xs sm:text-sm font-semibold text-[#B77945] uppercase tracking-wider">
                  {branch.tagline}
                </p>
              )}

              {/* Description */}
              <p className="mt-3.5 text-xs sm:text-sm text-[#756B64] leading-relaxed">
                {branch.description}
              </p>

              {/* Info Badges (Address & Hours) */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-4 border-t border-[#E8E0D6]">
                <div className="flex items-start gap-2.5 text-xs sm:text-sm text-[#2B1D17]">
                  <MapPin className="w-4 h-4 text-[#B77945] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold block text-[11px] uppercase tracking-wider text-[#756B64]">Lokasi</span>
                    <span>{branch.address}</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 text-xs sm:text-sm text-[#2B1D17]">
                  <Clock className="w-4 h-4 text-[#B77945] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold block text-[11px] uppercase tracking-wider text-[#756B64]">Jam Operasional</span>
                    <span>{branch.openingHours}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-7 pt-5 border-t border-[#E8E0D6] flex flex-wrap items-center gap-3">
              <a
                href={branchWALink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-xl bg-[#39271F] hover:bg-[#2B1D17] text-white font-semibold text-xs sm:text-sm tracking-wide flex items-center gap-2 shadow-[0_6px_16px_rgba(57,39,31,0.18)] transition-all"
              >
                <MessageCircle className="w-4 h-4 text-[#B77945]" />
                <span>Chat WhatsApp Cabang</span>
              </a>

              {branch.mapsUrl && (
                <a
                  href={branch.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 rounded-xl bg-[#F8F5EF] hover:bg-[#E8E0D6]/60 border border-[#E8E0D6] text-[#2B1D17] font-semibold text-xs sm:text-sm tracking-wide flex items-center gap-2 shadow-2xs transition-all"
                >
                  <Navigation className="w-4 h-4 text-[#B77945]" />
                  <span>Google Maps</span>
                  <ExternalLink className="w-3.5 h-3.5 text-[#756B64]" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Swipeable Gallery for Branch if photos are uploaded */}
        {branch.id !== 'chapter-5' && branch.galleryImages && branch.galleryImages.length > 0 && (
          <div className="mt-8">
            <LetGoCardsGallery
              images={branch.galleryImages}
              sectionLabel={`${branch.chapterName} (${branch.branchName})`}
            />
          </div>
        )}
      </div>
    </section>
  );
};
